import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  assertViajesFirmaPayload,
  VIAJES_FIRMA_EMAIL_PATTERN,
} from 'src/app/services/firma-digital/viajes-firma.mapper';
import {
  ExtraValuesPayload,
  OfflineOperation,
  OfflineStorageService,
  SignedPayload,
} from 'src/app/services/offline/offline-storage.service';
import { HormigonDispatchFacade } from '../../hormigon-dispatch.facade';

export interface PendingOperationEditDialogData {
  tkt: string;
  operationId: string;
  operation: OfflineOperation;
}

interface ReturnReasonOption {
  value: string;
  label: string;
}

@Component({
    selector: 'app-hormigon-pending-operation-edit-dialog',
    templateUrl: './pending-operation-edit-dialog.component.html',
    styleUrls: ['./pending-operation-edit-dialog.component.css'],
    standalone: false
})
export class HormigonPendingOperationEditDialogComponent {
  readonly operationType: OfflineOperation['type'];
  readonly tkt: string;
  readonly orderNumber: string;
  readonly emailPattern = VIAJES_FIRMA_EMAIL_PATTERN.source;

  signedPayload?: SignedPayload;
  extraValuesPayload?: ExtraValuesPayload;
  rutBody = '';
  rutDv = '';
  saving = false;
  errorMessage = '';
  returnReasons: ReturnReasonOption[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PendingOperationEditDialogData,
    private readonly dialogRef: MatDialogRef<HormigonPendingOperationEditDialogComponent>,
    private readonly offlineStorage: OfflineStorageService,
    private readonly dispatchFacade: HormigonDispatchFacade,
  ) {
    this.operationType = data.operation.type;
    const payload = this.clonePayload(data.operation.payload);
    this.tkt = String(payload.tkt);
    this.orderNumber = String(payload.orderNumber);

    if (this.isSignatureOperation()) {
      this.signedPayload = payload as SignedPayload;
      const normalizedRut = this.normalizeRut(this.signedPayload.rut);
      this.rutBody = normalizedRut.slice(0, -1);
      this.rutDv = normalizedRut.slice(-1).toUpperCase();
    } else if (this.operationType === 'extraValues') {
      this.extraValuesPayload = payload as ExtraValuesPayload;
      this.extraValuesPayload.files = this.extraValuesPayload.files ?? [];
      void this.loadReturnReasons();
    }
  }

  isSignatureOperation(): boolean {
    return this.operationType === 'signed' || this.operationType === 'signedCanvas';
  }

  isDigitalSignature(): boolean {
    return this.operationType === 'signedCanvas';
  }

  onRutBodyChange(value: string): void {
    this.rutBody = (value ?? '').replace(/\D/g, '').slice(0, 8);
  }

  onRutDvChange(value: string): void {
    this.rutDv = (value ?? '').replace(/[^0-9kK]/g, '').slice(0, 1).toUpperCase();
  }

  onRutKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
      'k', 'K', 'Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab',
    ];
    const inputValue = (event.target as HTMLInputElement).value;

    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
      return;
    }

    if (
      event.target instanceof HTMLInputElement &&
      event.target.name === 'rutBody' &&
      inputValue.length >= 8 &&
      !['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'].includes(event.key)
    ) {
      event.preventDefault();
    }
  }

  onSignatureChange(value: string): void {
    if (this.signedPayload) {
      this.signedPayload.signature = value;
    }
  }

  async save(): Promise<void> {
    if (this.saving) return;
    this.errorMessage = '';

    let payload: OfflineOperation['payload'];
    try {
      payload = this.buildValidatedPayload();
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : 'Revisa los datos ingresados.';
      return;
    }

    this.saving = true;
    try {
      const updated = await this.offlineStorage.updatePausedOperationPayload(
        this.data.tkt,
        this.data.operationId,
        payload,
      );
      if (!updated) {
        this.errorMessage = 'La operacion ya no esta pausada o dejo de existir.';
        return;
      }
      this.dialogRef.close(true);
    } catch (error) {
      this.errorMessage = error instanceof Error
        ? error.message
        : 'No se pudieron guardar los cambios locales.';
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private buildValidatedPayload(): OfflineOperation['payload'] {
    if (this.signedPayload) {
      const rut = `${this.rutBody}${this.rutDv}`;
      const payload: SignedPayload = {
        ...this.signedPayload,
        tkt: this.tkt,
        orderNumber: this.orderNumber,
        rut,
        name: this.signedPayload.name.trim(),
        email: (this.signedPayload.email ?? '').trim(),
      };

      this.assertSignatureFormPayload(payload);
      if (this.isDigitalSignature()) {
        assertViajesFirmaPayload(payload);
      }
      return payload;
    }

    if (this.extraValuesPayload) {
      return {
        ...this.extraValuesPayload,
        tkt: this.tkt,
        orderNumber: this.orderNumber,
        guidingRemarks: (this.extraValuesPayload.guidingRemarks ?? '').trim(),
        m3Returned: Number(this.extraValuesPayload.m3Returned ?? 0),
        m3ReturnedPallet: this.extraValuesPayload.m3ReturnedPallet ?? '',
        files: [...(this.extraValuesPayload.files ?? [])],
      };
    }

    throw new Error('Este tipo de operacion no se puede editar.');
  }

  private assertSignatureFormPayload(payload: SignedPayload): void {
    if (!payload.name) throw new Error('El nombre del firmante es obligatorio.');
    if (!/^\d{7,8}[0-9K]$/.test(payload.rut)) {
      throw new Error('El RUT del firmante debe tener 8 o 9 caracteres.');
    }
    if (/[kK]{2}/.test(payload.rut)) {
      throw new Error('El RUT del firmante no es valido.');
    }
    if (payload.email && !VIAJES_FIRMA_EMAIL_PATTERN.test(payload.email)) {
      throw new Error('El correo ingresado no es valido.');
    }
    if (this.isDigitalSignature()) {
      if (!payload.signature?.trim()) {
        throw new Error('La firma es obligatoria.');
      }
      if (!/^data:image\/png;base64,/i.test(payload.signature ?? '')) {
        throw new Error('La firma debe tener formato PNG: data:image/png;base64.');
      }
    } else if (!/^data:image\/(jpeg|png);base64,/i.test(payload.signature ?? '')) {
      throw new Error('Debes adjuntar una fotografia JPG o PNG de la guia.');
    }
  }


  private normalizeRut(value: string): string {
    return String(value ?? '').replace(/[^0-9kK]/g, '');
  }

  private async loadReturnReasons(): Promise<void> {
    try {
      const groupedReasons = await this.dispatchFacade
        .getRejectReasons()
        .toPromise() as any;
      const reasons = Array.isArray(groupedReasons)
        ? groupedReasons
        : [];
      this.returnReasons = reasons.map((reason: any) => {
        const text = typeof reason === 'string'
          ? reason
          : String(reason?.description ?? reason?.name ?? reason ?? '');
        return { value: text, label: text };
      }).filter((reason: ReturnReasonOption) => !!reason.value);

      const selected = this.extraValuesPayload?.reasonToReturn ?? '';
      if (selected && !this.returnReasons.some((reason) => reason.value === selected)) {
        this.returnReasons.unshift({ value: selected, label: selected });
      }
    } catch {
      const selected = this.extraValuesPayload?.reasonToReturn ?? '';
      this.returnReasons = selected ? [{ value: selected, label: selected }] : [];
    }
  }

  private clonePayload<T>(payload: T): T {
    return JSON.parse(JSON.stringify(payload)) as T;
  }
}
